import { BuildingsComposite } from '@data/geodata/building/BuildingsComposite';
import { GeoObject as GeoObjectLegacy } from '@data/geodata/legacy/GeoObject.js';
import { GridData } from '@data/GridData';
import { getNewColorScheme } from '@gui/store/persistent/persistentStore';
import type { GeodataDataType, GeodataJSON, GeoObjectMode } from '@visualizer/types';
import { IGeoObject, UUID } from '@visualizer/types';
import { FileFetcherV2 } from 'Loader/FileFetcherv2';
import { SerializationManager } from '../Loader/serialization/manager/SerializationManager';
import { patchDownloadQueueItem } from 'store/LoadingStore';
import { buildEmptySimulationSetId, EMPTY_SIMULATION_NAME } from './emptySimulationIdentity';
import { Ground } from './geodata/legacy/Ground';
import { Roads } from './geodata/legacy/Roads';
import { Targets } from './geodata/legacy/Targets.js';
import { TreesBatched } from './geodata/tree/TreesBatched';
import { LayerDownloadPlan, SimulationSet } from './SimulationSet';
import { ApiRoutes } from 'store/ApiRoutes';

type EnvironmentLegacy = {
    Ground?: Ground | null;
    Targets?: Targets | null;
    Roads?: Roads | null;
};

type Environment = {
    Buildings?: BuildingsComposite | null;
    Trees?: TreesBatched | null;
};

const DEFAULT_MODE: GeoObjectMode = 'standard';

type GeodataFileLike = typeof GeoObjectLegacy | 'Buildings' | 'Vegetation';

export class Geodata {
    id: UUID;
    created_at: Date;

    parentGrid: GridData;
    name: string;

    isOpen: boolean = true;

    filesMap: Map<GeodataFileLike, string> = new Map();

    dataObjectLegacy: EnvironmentLegacy = {};

    dataObject: Environment = {};

    simulationSets: SimulationSet[] = [];

    constructor(parentGrid: GridData, geodataJSON: GeodataJSON) {
        this.id = geodataJSON.id as UUID;
        this.parentGrid = parentGrid;
        this.created_at = new Date(geodataJSON.created_at);
        this.name = geodataJSON.name ? geodataJSON.name : 'NO-GEO-NAME';
        for (let simulationSetJSON of geodataJSON.simulations_sets) {
            const simulationSet = new SimulationSet(this, simulationSetJSON);
            this.simulationSets.push(simulationSet);
        }
        // Order Simulation Sets based on the number on their name. TODO: Would be best to have a "created_at" even here:
        this.simulationSets.sort((a, b) => {
            const getNumber = (name: string) => {
                const match = name.match(/\d+/);
                return match ? parseInt(match[0], 10) : 0;
            };

            return getNumber(a.name) - getNumber(b.name);
        });
    }

    prepareFiles(availableDataTypes: GeodataDataType[]) {
        this.filesMap.clear();
        availableDataTypes.forEach((dataType) => {
            const className = dataType.charAt(0).toUpperCase() + dataType.slice(1);
            this.filesMap.set(toClass(className), `${this.id}/${dataType}`);
        });
    }

    public getIteratorLegacy(): GeoObjectLegacy[] {
        return Object.values(this.dataObjectLegacy).filter((value) => value !== null);
    }

    public getIterator(): IGeoObject[] {
        return Object.values(this.dataObject).filter((value) => value !== null);
    }

    async discoverSimulationPlans(fileFetcher: FileFetcherV2): Promise<LayerDownloadPlan[][]> {
        return Promise.all(this.simulationSets.map((simulationSet) => simulationSet.discoverLayerPlans(fileFetcher)));
    }

    async loadData(fileFetcher: FileFetcherV2, simulationPlansBySet?: LayerDownloadPlan[][]) {
        const availableGeodata = await fileFetcher.availableGeodata(this.id);
        const requestDataTypes: GeodataDataType[] = ['ground', 'target', 'roads', 'vegetation', 'buildings'];
        const requestedDataTypes = requestDataTypes.filter((type) => availableGeodata[type] === true);
        fileFetcher.primeDownloadQueue(
            requestedDataTypes.map((dataType) => {
                const key = `${this.id}/${dataType}`;
                return {
                    key,
                    endpoint: ApiRoutes.geodataDownload(this.id, dataType),
                    label: `${this.name} · ${dataType}`,
                };
            })
        );

        const downloadResults = await Promise.all(
            requestedDataTypes.map(async (dataType) => {
                const key = `${this.id}/${dataType}`;
                const endpoint = ApiRoutes.geodataDownload(this.id, dataType);
                try {
                    await fileFetcher.downloadFile(key, endpoint);
                    return { dataType, available: true };
                } catch (error) {
                    if (isNotFoundError(error)) {
                        // Optional resource: treat as completed so download progress does not report a failure.
                        patchDownloadQueueItem(key, {
                            label: endpoint,
                            status: 'completed',
                            loadedBytes: 0,
                            totalBytes: 0,
                            rateBytesPerSecond: 0,
                            remainingSeconds: 0,
                        });
                        console.warn(`[Geodata] Optional data not available: ${dataType} (${endpoint})`);
                        return { dataType, available: false };
                    }
                    throw error;
                }
            })
        );

        const serializationManager = SerializationManager.geomSerializationManager;
        const cs = getNewColorScheme();
        const availableDataTypes = downloadResults.filter((result) => result.available).map((result) => result.dataType);
        this.prepareFiles(availableDataTypes);
        for (const [key, value] of this.filesMap) {
            if (key === 'Buildings') {
                const buildings = await serializationManager.deserializeGeom(value, 'Buildings', false);
                this.dataObject.Buildings = new BuildingsComposite({
                    name: 'Buildings',
                    colorScheme: cs,
                    data: buildings,
                    mode: DEFAULT_MODE,
                    individualBuildings: true,
                });
            } else {
                if (key === 'Vegetation') {
                    const trees = await serializationManager.deserializeGeom(value, 'Trees', false);
                    this.dataObject.Trees = new TreesBatched({
                        name: 'Trees',
                        colors: cs,
                        data: trees,
                        mode: DEFAULT_MODE,
                    });
                } else {
                    const legacyKey = key as typeof GeoObjectLegacy;
                    if (legacyKey === Ground) {
                        this.dataObjectLegacy.Ground = await serializationManager.deserializeGeom(value, Ground);
                    } else if (legacyKey === Roads) {
                        this.dataObjectLegacy.Roads = await serializationManager.deserializeGeom(value, Roads);
                    } else if (legacyKey === Targets) {
                        this.dataObjectLegacy.Targets = await serializationManager.deserializeGeom(value, Targets);
                    } else {
                        throw new Error(`Unsupported legacy geodata type: ${legacyKey?.name ?? String(legacyKey)}`);
                    }
                }
            }
        }
        if (this.dataObjectLegacy.Ground) {
            this.dataObjectLegacy.Ground.initializeGround(this.dataObjectLegacy.Targets);
        }

        if (this.simulationSets.length === 0) {
            this.createEmptySimulationSet();
        }
        const plansBySimulationSet =
            simulationPlansBySet ??
            (await Promise.all(this.simulationSets.map((simulationSet) => simulationSet.discoverLayerPlans(fileFetcher))));
        await Promise.all(
            this.simulationSets.map((simulationSet, index) => simulationSet.loadAllData(fileFetcher, plansBySimulationSet[index]))
        );
    }

    private createEmptySimulationSet() {
        const emptySimSetId = buildEmptySimulationSetId(this.id) as UUID;
        const emptySimulationSet = new SimulationSet(this, {
            id: emptySimSetId,
            name: 'empty-sim-set',
            created_at: new Date().toISOString(),
            geodata_id: this.id,
            simulations: [
                {
                    type: EMPTY_SIMULATION_NAME,
                    name: EMPTY_SIMULATION_NAME,
                    id: 0,
                    simulation_id: '0',
                },
            ],
        });

        this.simulationSets.push(emptySimulationSet);
    }

    public ensureAtLeastOneLayer() {
        if (this.simulationSets.length === 0) {
            this.createEmptySimulationSet();
        }

        const primarySimulationSet = this.simulationSets[0];
        if (!primarySimulationSet) {
            return undefined;
        }

        if (primarySimulationSet.layerCollection.length === 0) {
            primarySimulationSet.loadEmptyLayer();
        }

        return primarySimulationSet.layerCollection[0];
    }

    dispose() {
        this.getIterator().forEach((geoObject) => {
            geoObject.dispose();
        });
        this.getIteratorLegacy().forEach((geoObject) => {
            geoObject.dispose();
        });
    }
}

function isNotFoundError(error: unknown): boolean {
    if (error instanceof Error) {
        if (error.message.includes('(404)') || error.message.includes('404')) return true;
        const cause = (error as Error & { cause?: unknown }).cause;
        if (cause instanceof Error && (cause.message.includes('(404)') || cause.message.includes('404'))) {
            return true;
        }
    }
    return false;
}

function toClass(className: string): GeodataFileLike {
    switch (className) {
        case 'Buildings':
            return className;
        case 'Ground':
            return Ground;
        case 'Vegetation':
            return className;
        case 'Roads':
            return Roads;
        case 'Roads of the Noise Simulation':
            return Roads;
        case 'Target':
            return Targets;
        case 'Targets':
            return Targets;
        default:
            throw new Error('No class defined ' + className);
    }
}
